import React, { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { useAuth } from '../auth/AuthContext';

interface MfaScreenProps {
    email: string;
    password: string;
    onBack: () => void;
}

/**
 * Email MFA challenge: the supplier enters the code Cognito emailed after
 * their password was accepted. On success a session is issued. If the email is
 * delayed, "Resend code" re-initiates sign-in to issue a fresh code.
 */
export default function MfaScreen({ email, password, onBack }: MfaScreenProps) {
    const { setUser } = useAuth();
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        setNotice(null);
        if (!code.trim()) {
            setError('Enter the code we emailed you.');
            return;
        }
        setSubmitting(true);
        try {
            const user = await getAuthService().confirmSignIn(code.trim(), email);
            setUser(user);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Verification failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        setNotice(null);
        setResending(true);
        try {
            await getAuthService().resendMfaCode(email, password);
            setCode('');
            setNotice('A new code is on its way. It may take a moment to arrive.');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not resend the code.');
        } finally {
            setResending(false);
        }
    };

    return (
        <AuthCard title="Enter your code" subtitle={`We emailed a sign-in code to ${email}.`}>
            <SpaceBetween size="l">
                <FormField label="Verification code">
                    <Input
                        type="text"
                        value={code}
                        onChange={({ detail }) => setCode(detail.value)}
                    />
                </FormField>
                {notice ? <Alert type="info">{notice}</Alert> : null}
                {error ? <Alert type="error">{error}</Alert> : null}
                <Button variant="primary" loading={submitting} onClick={handleSubmit}>
                    Verify &amp; sign in
                </Button>
                <Button loading={resending} onClick={handleResend}>
                    Resend code
                </Button>
                <Button variant="link" onClick={onBack}>
                    Back to sign in
                </Button>
            </SpaceBetween>
        </AuthCard>
    );
}
